import React from 'react';
import { useQuery } from '@tanstack/react-query';
import type { ColumnDef, StockFeatures } from '@tanstack/react-table';
import {
  Card,
  CardHeader,
  CardContent,
  Flex,
  Heading,
  Text,
  Button,
  Box,
  Select,
  DataTable,
  LoadingOrbit,
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogFooter,
  TagBadge,
  type SelectOption,
  badgeVariantToTagColor,
} from '@chatsdet/ui';
import type { Pagination } from '@chatsdet/types';
import { API_URL } from '../../config.js';
import type { ExpectedRule } from '../../features/exploratory-tests/types';

type CandidateVerificationStatus = 'verified' | 'rejected' | 'error';
type IssueSeverity = 'low' | 'medium' | 'high' | 'unknown';

type CandidateVerificationRecord = {
  id: string;
  org: { id: string; name: string | null } | null;
  property: { id: string; name: string | null } | null;
  status: CandidateVerificationStatus;
  candidateDescription: string;
  candidateSeverity: IssueSeverity;
  verifiedTitle: string | null;
  rejectionReason: string | null;
  errorMessage: string | null;
  verificationDurationMs: number | null;
  createdAt: string;
};

type CandidateVerificationListResponse = {
  ok: true;
  data: CandidateVerificationRecord[];
  pagination: Pagination;
};

type CandidateVerificationDetailResponse = {
  ok: true;
  verification: {
    id: string;
    orgId: string;
    propertyId: string;
    canonicalPageId: string | null;
    credentialId: string | null;
    qaReportId: string | null;
    candidateDescription: string;
    candidateSeverity: IssueSeverity;
    candidateReproSteps: string;
    candidateObservedBehavior: string;
    candidateExpectedBehavior: string;
    candidateScreenshotLabel: string | null;
    candidateConsoleErrorSeen: boolean | null;
    candidateNetworkFailureSeen: boolean | null;
    candidateTodoId: string | null;
    verificationStatus: CandidateVerificationStatus;
    verifiedTitle: string | null;
    verifiedTldr: string | null;
    verifiedSummary: string | null;
    verifiedCategory: string | null;
    verifiedReproSteps: string | null;
    verifiedConfidence: number | null;
    screenshotStorageKey: string | null;
    filedIssueId: string | null;
    rejectionReason: string | null;
    rejectionConfidence: number | null;
    expectedRule: ExpectedRule | null;
    errorMessage: string | null;
    pageUrl: string | null;
    pageTitle: string | null;
    verificationDurationMs: number | null;
    createdAt: string;
  };
  organization: { id: string; name: string | null } | null;
  property: { id: string; name: string | null } | null;
};

const STATUS_OPTIONS: SelectOption[] = [
  { id: 'all', label: 'All statuses' },
  { id: 'verified', label: 'Verified' },
  { id: 'rejected', label: 'Rejected' },
  { id: 'error', label: 'Error' },
];

const STATUS_VARIANTS: Record<CandidateVerificationStatus, 'success' | 'warning' | 'destructive'> =
  {
    verified: 'success',
    rejected: 'warning',
    error: 'destructive',
  };

const SEVERITY_VARIANTS: Record<
  IssueSeverity,
  'destructive' | 'warning' | 'outline' | 'secondary'
> = {
  high: 'destructive',
  medium: 'warning',
  low: 'outline',
  unknown: 'secondary',
};

const formatDateTime = (value?: string | null) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return `${date.toLocaleDateString()} ${date.toLocaleTimeString()}`;
};

const formatDuration = (ms?: number | null) => {
  if (ms === null || ms === undefined) return '—';
  const seconds = Math.round(ms / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) {
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  }
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return `${hours}h ${remainingMinutes}m`;
};

function VerificationDetailContent({
  verificationId,
  onClose,
}: {
  verificationId: string;
  onClose: () => void;
}) {
  const detailQuery = useQuery({
    queryKey: ['superadmin-candidate-verification-detail', verificationId],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/superadmin/candidate-verifications/${verificationId}`, {
        credentials: 'include',
      });
      if (!res.ok) {
        throw new Error('Failed to load verification details');
      }
      return (await res.json()) as CandidateVerificationDetailResponse;
    },
    enabled: Boolean(verificationId),
  });

  const v = detailQuery.data?.verification;

  if (detailQuery.isLoading) {
    return (
      <DialogContent>
        <Flex align="center" justify="center" className="py-12">
          <LoadingOrbit size="lg" />
        </Flex>
      </DialogContent>
    );
  }

  if (detailQuery.isError) {
    return (
      <DialogContent>
        <Text className="text-destructive">
          {detailQuery.error instanceof Error
            ? detailQuery.error.message
            : 'Failed to load details'}
        </Text>
      </DialogContent>
    );
  }

  if (!v) return null;

  return (
    <>
      <DialogContent className="max-h-[70vh] overflow-y-auto">
        <Flex direction="column" gap="lg">
          {/* Status */}
          <Flex gap="md" align="center">
            <TagBadge
              name={v.verificationStatus}
              color={badgeVariantToTagColor(STATUS_VARIANTS[v.verificationStatus])}
              hideDot
            />
            <TagBadge
              name={v.candidateSeverity}
              color={badgeVariantToTagColor(SEVERITY_VARIANTS[v.candidateSeverity])}
              hideDot
            />
            {v.verificationDurationMs && (
              <Text size="sm" className="text-muted-foreground">
                Duration: {formatDuration(v.verificationDurationMs)}
              </Text>
            )}
          </Flex>

          {/* Context */}
          <Box className="space-y-2">
            <Heading level={3}>Context</Heading>
            <Flex direction="column" gap="xs">
              <Text size="sm">
                <span className="text-muted-foreground">Page URL:</span> {v.pageUrl ?? '—'}
              </Text>
              <Text size="sm">
                <span className="text-muted-foreground">Page Title:</span> {v.pageTitle ?? '—'}
              </Text>
              <Text size="sm">
                <span className="text-muted-foreground">QA Report ID:</span> {v.qaReportId ?? '—'}
              </Text>
              <Text size="sm">
                <span className="text-muted-foreground">Created:</span>{' '}
                {formatDateTime(v.createdAt)}
              </Text>
            </Flex>
          </Box>

          {/* Candidate Input */}
          <Box className="space-y-2">
            <Heading level={3}>Candidate Input</Heading>
            <Flex direction="column" gap="md">
              <Box>
                <Text size="xs" className="text-muted-foreground mb-1">
                  Description
                </Text>
                <Text size="sm">{v.candidateDescription}</Text>
              </Box>
              <Box>
                <Text size="xs" className="text-muted-foreground mb-1">
                  Reproduction Steps
                </Text>
                <Text size="sm" className="whitespace-pre-wrap font-mono text-xs">
                  {v.candidateReproSteps}
                </Text>
              </Box>
              <Flex gap="lg">
                <Box className="flex-1">
                  <Text size="xs" className="text-muted-foreground mb-1">
                    Expected Behavior
                  </Text>
                  <Text size="sm">{v.candidateExpectedBehavior}</Text>
                </Box>
                <Box className="flex-1">
                  <Text size="xs" className="text-muted-foreground mb-1">
                    Observed Behavior
                  </Text>
                  <Text size="sm">{v.candidateObservedBehavior}</Text>
                </Box>
              </Flex>
              <Flex gap="md">
                {v.candidateConsoleErrorSeen && (
                  <TagBadge name="Console Error" color="gray" hideDot />
                )}
                {v.candidateNetworkFailureSeen && (
                  <TagBadge name="Network Failure" color="gray" hideDot />
                )}
              </Flex>
            </Flex>
          </Box>

          {/* Verification Outcome */}
          {v.verificationStatus === 'verified' && (
            <Box className="border border-success/50 rounded-md p-4 bg-success/5">
              <Text size="sm" weight="medium" className="mb-2 text-success">
                Verified Issue
              </Text>
              <Flex direction="column" gap="md">
                <Box>
                  <Text size="xs" className="text-muted-foreground mb-1">
                    Title
                  </Text>
                  <Text size="sm" weight="medium">
                    {v.verifiedTitle}
                  </Text>
                </Box>
                {v.verifiedTldr && (
                  <Box>
                    <Text size="xs" className="text-muted-foreground mb-1">
                      TL;DR
                    </Text>
                    <Text size="sm">{v.verifiedTldr}</Text>
                  </Box>
                )}
                {v.verifiedSummary && (
                  <Box>
                    <Text size="xs" className="text-muted-foreground mb-1">
                      Summary
                    </Text>
                    <Text size="sm" className="whitespace-pre-wrap">
                      {v.verifiedSummary}
                    </Text>
                  </Box>
                )}
                {v.verifiedReproSteps && (
                  <Box>
                    <Text size="xs" className="text-muted-foreground mb-1">
                      Verified Repro Steps
                    </Text>
                    <Text size="sm" className="whitespace-pre-wrap font-mono text-xs">
                      {v.verifiedReproSteps}
                    </Text>
                  </Box>
                )}
                <Flex gap="md">
                  {v.verifiedCategory && (
                    <Text size="sm">
                      <span className="text-muted-foreground">Category:</span> {v.verifiedCategory}
                    </Text>
                  )}
                  {v.verifiedConfidence && (
                    <Text size="sm">
                      <span className="text-muted-foreground">Confidence:</span>{' '}
                      {v.verifiedConfidence}%
                    </Text>
                  )}
                  {v.filedIssueId && (
                    <Text size="sm">
                      <span className="text-muted-foreground">Filed Issue:</span> {v.filedIssueId}
                    </Text>
                  )}
                </Flex>
              </Flex>
            </Box>
          )}

          {v.verificationStatus === 'rejected' && v.expectedRule && (
            <Box className="border border-warning/50 rounded-md p-4 bg-warning/5">
              <Flex align="center" gap="sm" className="mb-2">
                <TagBadge
                  name="Expected — matches a rule"
                  color={badgeVariantToTagColor('warning')}
                  hideDot
                />
              </Flex>
              <Text size="sm">{v.expectedRule.statement}</Text>
              {/* No link here: this is a cross-org superadmin surface and /lookup/$id is scoped
                  to the viewer's own org, so it would 404/misresolve for other orgs' rows. */}
              <Text size="sm" className="mt-2 text-muted-foreground">
                {v.expectedRule.capabilityCode
                  ? `${v.expectedRule.capabilityCode} · ${v.expectedRule.capabilityTitle}`
                  : v.expectedRule.capabilityTitle}
              </Text>
              {v.rejectionReason && (
                <Box className="mt-3 pt-3 border-t border-warning/30">
                  <Text size="xs" className="text-muted-foreground mb-1">
                    Verifier summary
                  </Text>
                  <Text size="sm">{v.rejectionReason}</Text>
                </Box>
              )}
            </Box>
          )}

          {v.verificationStatus === 'rejected' && !v.expectedRule && (
            <Box className="border border-warning/50 rounded-md p-4 bg-warning/5">
              <Text size="sm" weight="medium" className="mb-2 text-warning">
                Rejection Reason
              </Text>
              <Text size="sm">{v.rejectionReason ?? 'No reason provided'}</Text>
              {v.rejectionConfidence && (
                <Text size="sm" className="mt-2 text-muted-foreground">
                  Confidence: {v.rejectionConfidence}%
                </Text>
              )}
            </Box>
          )}

          {v.verificationStatus === 'error' && (
            <Box className="border border-destructive/50 rounded-md p-4 bg-destructive/5">
              <Text size="sm" weight="medium" className="mb-2 text-destructive">
                Error
              </Text>
              <Text size="sm">{v.errorMessage ?? 'Unknown error'}</Text>
            </Box>
          )}
        </Flex>
      </DialogContent>
      <DialogFooter>
        <Button variant="outline" onClick={onClose}>
          Close
        </Button>
      </DialogFooter>
    </>
  );
}

export function CandidateVerificationsTab() {
  const [statusFilter, setStatusFilter] = React.useState<SelectOption['id']>('all');
  const [page, setPage] = React.useState(1);
  const [selectedVerificationId, setSelectedVerificationId] = React.useState<string | null>(null);
  const pageSize = 20;

  React.useEffect(() => {
    setPage(1);
  }, [statusFilter]);

  const verificationsQuery = useQuery({
    queryKey: ['superadmin-candidate-verifications', statusFilter, page],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.set('page', page.toString());
      params.set('pageSize', pageSize.toString());
      if (statusFilter !== 'all') {
        params.set('status', String(statusFilter));
      }

      const res = await fetch(
        `${API_URL}/superadmin/candidate-verifications?${params.toString()}`,
        {
          credentials: 'include',
        }
      );
      if (!res.ok) {
        throw new Error('Failed to load candidate verifications');
      }
      return (await res.json()) as CandidateVerificationListResponse;
    },
  });

  const columns = React.useMemo<ColumnDef<StockFeatures, CandidateVerificationRecord>[]>(() => {
    return [
      {
        header: 'Description',
        accessorKey: 'candidateDescription',
        cell: ({ row }) => (
          <Flex direction="column" gap="xs">
            <Text weight="medium" className="line-clamp-2">
              {row.original.verifiedTitle ?? row.original.candidateDescription.slice(0, 100)}
            </Text>
            <Text size="xs" className="text-muted-foreground">
              ID: {row.original.id.slice(0, 8)}...
            </Text>
          </Flex>
        ),
      },
      {
        header: 'Organization',
        accessorKey: 'org',
        cell: ({ row }) => {
          if (!row.original.org) {
            return <Text>—</Text>;
          }
          return (
            <Flex direction="column">
              <Text weight="medium">{row.original.org.name ?? 'Unnamed org'}</Text>
              {row.original.property && (
                <Text size="xs" className="text-muted-foreground">
                  {row.original.property.name ?? 'Unnamed property'}
                </Text>
              )}
            </Flex>
          );
        },
      },
      {
        header: 'Status',
        accessorKey: 'status',
        cell: ({ row }) => (
          <Flex direction="column" gap="xs">
            <TagBadge
              name={row.original.status}
              color={badgeVariantToTagColor(STATUS_VARIANTS[row.original.status])}
              hideDot
            />
            <TagBadge
              name={row.original.candidateSeverity}
              color={badgeVariantToTagColor(SEVERITY_VARIANTS[row.original.candidateSeverity])}
              hideDot
              size="sm"
            />
          </Flex>
        ),
      },
      {
        header: 'Outcome',
        accessorKey: 'verifiedTitle',
        cell: ({ row }) => {
          if (row.original.status === 'verified') {
            return (
              <Text size="sm" className="text-success line-clamp-2">
                {row.original.verifiedTitle ?? 'Issue filed'}
              </Text>
            );
          }
          if (row.original.status === 'rejected') {
            return (
              <Text size="sm" className="text-warning line-clamp-2">
                {row.original.rejectionReason ?? 'Could not reproduce'}
              </Text>
            );
          }
          return (
            <Text size="sm" className="text-destructive line-clamp-2">
              {row.original.errorMessage ?? 'Error occurred'}
            </Text>
          );
        },
      },
      {
        header: 'Duration',
        accessorKey: 'verificationDurationMs',
        cell: ({ row }) => (
          <Text size="sm">{formatDuration(row.original.verificationDurationMs)}</Text>
        ),
      },
      {
        header: 'Created',
        accessorKey: 'createdAt',
        cell: ({ row }) => (
          <Text size="sm" className="text-muted-foreground">
            {formatDateTime(row.original.createdAt)}
          </Text>
        ),
      },
    ];
  }, []);

  const pagination = verificationsQuery.data?.pagination;
  const canPrevious = page > 1;
  const canNext = pagination ? page < (pagination.totalPages || 0) : false;

  return (
    <>
      <Card padding="lg">
        <CardHeader>
          <Flex align="center" justify="between" wrap gap="md">
            <div>
              <Heading level={3}>Candidate Verifications</Heading>
              <Text size="sm" className="text-muted-foreground mt-1">
                Audit trail of all candidate issue verification attempts.
              </Text>
            </div>
          </Flex>
        </CardHeader>
        <CardContent>
          <Flex gap="md" wrap className="mb-4">
            <Select
              label="Status"
              items={STATUS_OPTIONS}
              selectedKey={statusFilter}
              onSelectionChange={(key) => setStatusFilter(String(key))}
              className="min-w-[200px]"
            />
          </Flex>

          {verificationsQuery.isLoading ? (
            <Flex align="center" justify="center" className="py-12">
              <LoadingOrbit size="lg" />
            </Flex>
          ) : verificationsQuery.isError ? (
            <Text className="text-destructive">
              {verificationsQuery.error instanceof Error
                ? verificationsQuery.error.message
                : 'Failed to load verifications'}
            </Text>
          ) : verificationsQuery.data && verificationsQuery.data.data.length > 0 ? (
            <DataTable
              columns={columns}
              data={verificationsQuery.data.data}
              onRowClick={(row) => setSelectedVerificationId(row.id)}
            />
          ) : (
            <Text className="text-muted-foreground py-6">No verifications found.</Text>
          )}

          <Flex align="center" justify="between" className="mt-4">
            <Text size="sm" className="text-muted-foreground">
              Page {pagination?.page ?? 1} of {pagination?.totalPages ?? 1} (
              {pagination?.totalItems ?? 0} total)
            </Text>
            <Flex gap="sm">
              <Button
                size="sm"
                variant="outline"
                isDisabled={!canPrevious || verificationsQuery.isFetching}
                onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
              >
                Previous
              </Button>
              <Button
                size="sm"
                variant="outline"
                isDisabled={!canNext || verificationsQuery.isFetching}
                onClick={() => setPage((prev) => prev + 1)}
              >
                Next
              </Button>
            </Flex>
          </Flex>
        </CardContent>
      </Card>

      {selectedVerificationId && (
        <DialogTrigger
          isOpen
          onOpenChange={(isOpen) => !isOpen && setSelectedVerificationId(null)}
          size="xl"
        >
          <span />
          <Dialog title="Verification Details">
            <VerificationDetailContent
              verificationId={selectedVerificationId}
              onClose={() => setSelectedVerificationId(null)}
            />
          </Dialog>
        </DialogTrigger>
      )}
    </>
  );
}
